import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { graphql, Link, useStaticQuery } from 'gatsby';
import { useMemo, useRef, useState } from 'react';
import DOMPurify from 'isomorphic-dompurify';

// @todo remove header.css import after refactoring
import '../../styles/generic.css';
import '../../styles/header.css';
import '../../styles/news-highlights.css';

import { _ } from '../../utils/l10n';
import { ReadMoreLink } from '../generic/ReadMoreLink';
import ChevronLeftWhite from '../../assets/icons/chevron-left.svg';

export const NewsHighlightsCarousel = () => {
  const { allFile: { nodes: highlights } } : Queries.HomePageNewsQuery = useStaticQuery(graphql`
    query HomePageNews {
      allFile(
        filter: {
          sourceInstanceName: {eq: "content"},
          relativeDirectory: {eq: "news"},
          name: {ne: "index"},
          childMdx: { frontmatter: {
            highlight_on_homepage: {eq: true},
            date: {ne: ""},
            image: { name: {ne: null} },
          } }
        }
        sort: {childMdx: {frontmatter: {date: DESC}}}
      ) {
        nodes {
          id
          childMdx {
            frontmatter {
              title
              news_slug
              image_alt
              image {
                childImageSharp {
                  gatsbyImageData(placeholder: NONE)
                }
              }
              image_dark {
                childImageSharp {
                  gatsbyImageData(placeholder: NONE)
                }
              }
            }
          }
        }
      }
    }
  `);

  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  const progressBarRef = useRef<HTMLDivElement>(null);

  const mouseEnterHandler = () => {
    if (progressBarRef.current) {
      progressBarRef.current.style['animationPlayState'] = 'paused';
    }
  }

  const mouseLeaveHandler = () => {
    if (progressBarRef.current) {
      progressBarRef.current.style['animationPlayState'] = 'running';
    }
  }

  const lastSlideIndex = useMemo(() => highlights.length - 1, [highlights])

  const getNextSlideIndex = (currentIndex: number) => {
    if (currentIndex < lastSlideIndex) {
      return currentIndex + 1;
    }
    return 0;
  }

  const getPreviousSlideIndex = (currentIndex: number) => {
    if (currentIndex === 0) {
      return lastSlideIndex;
    }
    return currentIndex - 1;
  }

  const updateCurrentSlideIndex = (getIndex: (currentIndex: number) => number) => {
    const newIndex = getIndex(currentSlideIndex);
    setCurrentSlideIndex(newIndex)
  }

  const slides = useMemo(() => highlights.map((highlight, index) => {
    // @todo Investigate why properties are not recognized in the auto-generated type
    // @ts-ignore "Property frontmatter does not exist on type..."
    const { id, childMdx: { frontmatter: { title, news_slug, image_alt, image_dark, image } } } = highlight;
    const imageData = getImage(image_dark ?? image);
    const isCurrentSlide = currentSlideIndex === index;
    const slides = [];
    slides.push(
      <li
        key={id}
        className={`news-highlights-slide ${isCurrentSlide ? 'current-slide' : ''}`}
        onMouseEnter={mouseEnterHandler}
        onMouseLeave={mouseLeaveHandler}
      >
        <div className='news-highlights-slide-info-container'>
          <div className='news-highlights-slide-info'>
            <div className='news-highlights-index'>{(index + 1).toString().padStart(2, '0')}</div>
            <span className='title-5' dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(title) }} />
            <ReadMoreLink navigateTo={`/about/news/${news_slug}/`} />
          </div>
          <div className='news-highlights-slide-arrows'>
            <button
              className='news-highlights-slide-arrow'
              onClick={() => updateCurrentSlideIndex(getPreviousSlideIndex)}
            >
              <img src={ChevronLeftWhite} alt='chevron-left' className='chevron-icon' />
            </button>
            <button
              className='news-highlights-slide-arrow'
              onClick={() => updateCurrentSlideIndex(getNextSlideIndex)}
            >
              <img src={ChevronLeftWhite} alt='chevron-right' className='chevron-icon' />
            </button>
          </div>
        </div>
        <Link to={`/about/news/${news_slug}/`} className='news-highlight-image-container'>
          {imageData && (
            <GatsbyImage
              image={imageData}
              alt={image_alt ?? ''}
              loading={index === 0 ? 'eager' : 'lazy'}
              className='news-highlight-image'
              objectFit='cover'
              objectPosition='bottom right'
            />
          )}
          <div
            ref={isCurrentSlide ? progressBarRef : undefined}
            className={`${isCurrentSlide ? 'news-highlights-slide-progress-bar' : undefined}`}
            onAnimationEnd={() => updateCurrentSlideIndex(getNextSlideIndex)}
          >
            <div />
          </div>
        </Link>
      </li>
    );

    return slides;
  }), [currentSlideIndex]);

  return (
    <ul className='news-highlights-carousel'>
      {slides}
    </ul>
  );
}
